/**
 * useSkillFiles - In-memory file store for a skill's inline file bundle.
 *
 * The skill model stores a skill's files as an inline bundle
 * (Array<{ path, content }>) on the item row. This hook is a pure in-memory
 * store seeded from an initial files array — NO network calls. The owning
 * component is responsible for seeding it from the loaded item's `files` and
 * persisting the whole array via PATCH.
 */
import { useCallback, useState } from "react";

export interface SkillFile {
  path: string;
  content: string;
}

/**
 * Legacy file-metadata shape. Retained as a type-only export because
 * SkillFileViewer.tsx and SkillsPage/types.ts still import it. The in-memory
 * store no longer produces these — they describe the old per-file metadata API.
 */
export interface SkillFileInfo {
  id: string;
  path: string;
  filename: string;
  contentType: string;
  sizeBytes: number | null;
  isBinary: boolean;
  checksum: string | null;
}

export function useSkillFiles(initial: SkillFile[] = []) {
  const [files, setFiles] = useState<SkillFile[]>(initial);

  const reset = useCallback((next: SkillFile[]) => setFiles(next), []);

  const getContent = useCallback(
    (path: string) => files.find((f) => f.path === path)?.content,
    [files]
  );

  const createFile = useCallback((path: string, content = "") => {
    setFiles((prev) =>
      prev.some((f) => f.path === path) ? prev : [...prev, { path, content }]
    );
  }, []);

  const updateFile = useCallback((path: string, content: string) => {
    setFiles((prev) =>
      prev.map((f) => (f.path === path ? { ...f, content } : f))
    );
  }, []);

  const deleteFile = useCallback((path: string) => {
    setFiles((prev) => prev.filter((f) => f.path !== path));
  }, []);

  return { files, reset, getContent, createFile, updateFile, deleteFile };
}
